import * as React from "react";

/**
 * `localStorage` key the root `<ThemeProvider>` persists light/dark/system to.
 *
 * Also hard-coded in `index.html`'s pre-paint script (a plain .html file can't
 * call into the bundle), so the first frame already renders in the resolved
 * theme instead of flashing light and correcting. Keep the two in sync.
 */
export const THEME_STORAGE_KEY = "aisquare-ui-playground:theme";

/** Matching key for the direction toggle, likewise mirrored in `index.html`. */
export const DIR_STORAGE_KEY = "aisquare-ui-playground:dir";

export type Dir = "ltr" | "rtl";

function readDir(): Dir {
  if (typeof window === "undefined") return "ltr";
  try {
    return window.localStorage.getItem(DIR_STORAGE_KEY) === "rtl" ? "rtl" : "ltr";
  } catch {
    return "ltr";
  }
}

/**
 * Writing direction for the whole page, persisted so a QA pass can reload
 * without dropping back to LTR.
 *
 * Theme is deliberately NOT here: it comes from the library's own
 * `<ThemeProvider>` / `useTheme()` (mounted in `main.tsx`), so the playground
 * exercises the same light/dark/system cascade a consumer app gets instead of
 * running a second, parallel implementation of it. Direction has no library
 * equivalent, so it stays local.
 */
export function useDirection() {
  const [dir, setDir] = React.useState<Dir>(readDir);

  React.useEffect(() => {
    document.documentElement.setAttribute("dir", dir);
    try {
      window.localStorage.setItem(DIR_STORAGE_KEY, dir);
    } catch {
      // Storage unavailable (private mode). The attribute is still applied.
    }
  }, [dir]);

  return {
    dir,
    toggleDir: () => setDir((d) => (d === "rtl" ? "ltr" : "rtl")),
  };
}
